import { useState, type ReactNode } from 'react';
import { JEV_CORRECTION_LABELS, JEV_EVIDENCE, JEV_EVIDENCE_LABELS, JEV_FLAG_CORRECTION_CODES, JEV_PART_CORRECTION_CODES, type CorrectJevRequest, type JevCorrectionCode, type JevCorrectionView, type JevEvidence } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { ModuleGate } from '../../../components/ModuleGate';
import { MenuGroup, MenuItem, Popover, usePopover } from '../../../components/Popover';
import { correctionLine } from '../api/jev-api';
import styles from './CorrectJevMenu.module.css';

// F23.19 (ADR 0059): one click says what Jev got right or wrong about a part, or about the flags. It saves at once and
// the screen offers Undo; Jev's own answer and the order never change (DECISIONS-LOG 2026-10-25 Q6).

const POPOVER_WIDTH = 300;

interface CorrectJevMenuProps {
  /** What is being corrected, for the button's name: "Revit", "the flags". */
  readonly subject: string;
  /** Null is the flags box. */
  readonly partIndex: number | null;
  /** Jev's grade for the part, which can't be picked as the "right" one. */
  readonly currentGrade: JevEvidence | null;
  readonly onCorrect: (input: CorrectJevRequest) => void;
}

export function CorrectJevMenu({ subject, partIndex, currentGrade, onCorrect }: CorrectJevMenuProps): ReactNode {
  return (
    <ModuleGate module="jev">
      <Menu subject={subject} partIndex={partIndex} currentGrade={currentGrade} onCorrect={onCorrect} />
    </ModuleGate>
  );
}

function Menu({ subject, partIndex, currentGrade, onCorrect }: CorrectJevMenuProps): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const [note, setNote] = useState('');
  const [isPickingGrade, setPickingGrade] = useState(false);
  const codes = partIndex === null ? JEV_FLAG_CORRECTION_CODES : JEV_PART_CORRECTION_CODES;

  const finish = (input: Omit<CorrectJevRequest, 'partIndex' | 'note'>): void => {
    onCorrect({ partIndex, note: note.trim() === '' ? null : note, ...input });
    setNote('');
    setPickingGrade(false);
    close();
  };
  const pickCode = (code: JevCorrectionCode): void => {
    if (code === 'WRONG_GRADE') setPickingGrade(true);
    else finish({ code });
  };
  const closeMenu = (): void => {
    setPickingGrade(false);
    close();
  };

  return (
    <>
      <Button ref={triggerRef} variant="ghost" size="sm" aria-haspopup="dialog" aria-expanded={anchor !== null} aria-label={`Correct what Jev found about ${subject}`} onClick={toggle}>
        Correct
      </Button>
      <Popover anchor={anchor} onClose={closeMenu} label={`Correct what Jev found about ${subject}`} width={POPOVER_WIDTH}>
        {isPickingGrade ? (
          <MenuGroup label="The right grade is">
            {JEV_EVIDENCE.map((grade, level) => (
              <MenuItem key={grade} disabled={grade === currentGrade} meta={grade === currentGrade ? 'Jev’s grade' : undefined} onClick={() => finish({ code: 'WRONG_GRADE', correctedLevel: level })}>
                {JEV_EVIDENCE_LABELS[grade]}
              </MenuItem>
            ))}
            <MenuItem onClick={() => setPickingGrade(false)}>Back</MenuItem>
          </MenuGroup>
        ) : (
          <div className={styles.menu}>
            <div className={styles.note}>
              <Input value={note} maxLength={500} placeholder="Add a note (optional)" aria-label="Note for this correction" onChange={(event) => setNote(event.target.value)} />
            </div>
            <MenuGroup>
              {codes.map((code) => (
                <MenuItem key={code} onClick={() => pickCode(code)}>
                  {JEV_CORRECTION_LABELS[code]}
                </MenuItem>
              ))}
            </MenuGroup>
          </div>
        )}
      </Popover>
    </>
  );
}

/** "Marked by Mahin: The grade is wrong → Shown in real work", with the note muted beneath. Nothing when there are none. */
export function CorrectionLines({ corrections }: { readonly corrections: readonly JevCorrectionView[] }): ReactNode {
  if (corrections.length === 0) return null;
  return (
    <ul className={styles.lines} aria-label="What recruiters said about this">
      {corrections.map((correction) => (
        <li key={correction.id} className={styles.line}>
          <span className={styles.by}>Marked by {correction.byName ?? 'someone'}:</span> {correctionLine(correction)}
          {correction.note && <span className={styles.lineNote}>{correction.note}</span>}
        </li>
      ))}
    </ul>
  );
}
